/**
 * 单位与建筑的数值表。
 *
 * 数值来源：01_核心设计/六大阵营与单位详表.md（人民团结阵线）
 * 造价由 03_数值平衡 的 CV 公式生成后人工微调。
 *
 * 纪律：这里是「数据」，不是「逻辑」。任何 if/else 的行为分支都应写进 systems，
 * 通过 flags 字段驱动，避免数据表逐渐长成第二套代码。
 */

import { Armor, Damage, Kind, TICKS_PER_SEC } from '../../shared/constants.ts';
import { fromFloat, type Fix } from '../fixed/fix.ts';

export const enum Flag {
  None = 0,
  /** 疾行：出厂即满速 */
  Swift = 1 << 0,
  /** 护卫：挡线 + 友军减伤光环 */
  Guard = 1 << 1,
  /** 碾压步兵 */
  Crush = 1 << 2,
  /** 可建造建筑 */
  Builder = 1 << 3,
  /** 可采集 */
  CanHarvest = 1 << 4,
  /** 发电 */
  PowerPlant = 1 << 5,
  /** 资源卸货点 */
  Refinery = 1 << 6,
  /** 生产建筑 */
  Producer = 1 << 7,
  /** 提供雷达（Intel 产出 + 小地图） */
  Radar = 1 << 8,
  /** 无法移动 */
  Immobile = 1 << 9,
  /** 主基地：摧毁即败 */
  HQ = 1 << 10,
  /** 摧毁时爆炸（核反应堆） */
  ExplodeOnDeath = 1 << 11,
  /** 使用投射物而非瞬时命中 */
  Projectile = 1 << 12,
  /** 维修友方建筑与载具 */
  Repairer = 1 << 13,
}

export interface UnitDef {
  id: number;
  key: string;
  name: string;
  kind: Kind;
  tier: 1 | 2 | 3 | 4;
  /** 建造分类页 */
  tab: 'building' | 'infantry' | 'vehicle';
  costSeu: number;
  costIntel: number;
  pop: number;
  buildSeconds: number;
  hp: number;
  armor: Armor;
  /** 每秒伤害；实际单次伤害 = dps * reloadSeconds */
  dps: number;
  dmgType: Damage;
  /** 格 */
  range: number;
  reloadSeconds: number;
  /** 溅射半径（格），0 为单体 */
  splash: number;
  /** 格/秒 */
  speed: number;
  sight: number;
  /** 碰撞半径（格） */
  radius: number;
  flags: number;
  /** 建筑占地（格），非建筑为 0 */
  footprint: number;
  /** 电力：正数产出，负数消耗 */
  power: number;
  /** 需要的前置建筑 key，全部满足才可建造 */
  requires: string[];
  desc: string;
  /** 渲染用主色 */
  color: number;
  /** 维修速率（HP/秒），0 表示不能维修 */
  repairPerSec: number;
}

let nextId = 1;

function def(d: Omit<UnitDef, 'id' | 'repairPerSec'> & { repairPerSec?: number }): UnitDef {
  return { ...d, repairPerSec: d.repairPerSec ?? 0, id: nextId++ };
}

// ─────────────────────────────────────────────────────────────
// 人民团结阵线 —— 建筑
// ─────────────────────────────────────────────────────────────

export const DEFS: UnitDef[] = [
  def({
    key: 'conyard',
    name: '建造厂',
    kind: Kind.Building,
    tier: 1,
    tab: 'building',
    costSeu: 0,
    costIntel: 0,
    pop: 0,
    buildSeconds: 0,
    hp: 3000,
    armor: Armor.Structure,
    dps: 0,
    dmgType: Damage.Kinetic,
    range: 0,
    reloadSeconds: 1,
    splash: 0,
    speed: 0,
    sight: 8,
    radius: 1.5,
    flags: Flag.Immobile | Flag.HQ | Flag.Builder | Flag.Producer,
    footprint: 3,
    power: 0,
    requires: [],
    desc: '建造核心。摧毁即战败。',
    color: 0xc44536,
  }),
  def({
    key: 'reactor',
    name: '核裂变反应堆',
    kind: Kind.Building,
    tier: 1,
    tab: 'building',
    costSeu: 500,
    costIntel: 0,
    pop: 0,
    buildSeconds: 10,
    hp: 700,
    armor: Armor.Structure,
    dps: 0,
    dmgType: Damage.Kinetic,
    range: 0,
    reloadSeconds: 1,
    splash: 0,
    speed: 0,
    sight: 5,
    radius: 1,
    flags: Flag.Immobile | Flag.PowerPlant | Flag.ExplodeOnDeath,
    footprint: 2,
    power: 60,
    requires: [],
    desc: '+60 电力。摧毁时爆炸并瘫痪周边建筑。',
    color: 0x4fa3d1,
  }),
  def({
    key: 'refinery',
    name: '物资精炼厂',
    kind: Kind.Building,
    tier: 1,
    tab: 'building',
    costSeu: 1600,
    costIntel: 0,
    pop: 0,
    buildSeconds: 20,
    hp: 1400,
    armor: Armor.Structure,
    dps: 0,
    dmgType: Damage.Kinetic,
    range: 0,
    reloadSeconds: 1,
    splash: 0,
    speed: 0,
    sight: 6,
    radius: 1.5,
    flags: Flag.Immobile | Flag.Refinery,
    footprint: 3,
    power: -20,
    requires: [],
    desc: 'SEU 卸货点，附赠 1 辆拾荒车。',
    color: 0xd9a441,
  }),
  def({
    key: 'barracks',
    name: '国民警卫军兵营',
    tier: 1,
    kind: Kind.Building,
    tab: 'building',
    costSeu: 500,
    costIntel: 0,
    pop: 0,
    buildSeconds: 12,
    hp: 900,
    armor: Armor.Structure,
    dps: 0,
    dmgType: Damage.Kinetic,
    range: 0,
    reloadSeconds: 1,
    splash: 0,
    speed: 0,
    sight: 6,
    radius: 1,
    flags: Flag.Immobile | Flag.Producer,
    footprint: 2,
    power: -15,
    requires: ['reactor'],
    desc: '步兵生产。',
    color: 0xb0603a,
  }),
  def({
    key: 'pillbox',
    name: '机枪碉堡',
    kind: Kind.Building,
    tier: 1,
    tab: 'building',
    costSeu: 400,
    costIntel: 0,
    pop: 0,
    buildSeconds: 8,
    hp: 850,
    armor: Armor.Structure,
    dps: 34,
    dmgType: Damage.Kinetic,
    range: 7,
    reloadSeconds: 0.6,
    splash: 0,
    speed: 0,
    sight: 8,
    radius: 0.9,
    flags: Flag.Immobile,
    footprint: 1,
    power: -10,
    requires: ['barracks'],
    desc: '反步兵防御。动能伤害，对重甲效果差。',
    color: 0x8a8f7a,
  }),
  def({
    key: 'factory',
    name: '战车工厂',
    kind: Kind.Building,
    tier: 2,
    tab: 'building',
    costSeu: 2000,
    costIntel: 0,
    pop: 0,
    buildSeconds: 25,
    hp: 1200,
    armor: Armor.Structure,
    dps: 0,
    dmgType: Damage.Kinetic,
    range: 0,
    reloadSeconds: 1,
    splash: 0,
    speed: 0,
    sight: 6,
    radius: 1.5,
    flags: Flag.Immobile | Flag.Producer,
    footprint: 3,
    power: -30,
    requires: ['refinery'],
    desc: '载具生产。',
    color: 0x9c5230,
  }),
  def({
    key: 'radar',
    name: '观测站',
    kind: Kind.Building,
    tier: 2,
    tab: 'building',
    costSeu: 1000,
    costIntel: 0,
    pop: 0,
    buildSeconds: 15,
    hp: 900,
    armor: Armor.Structure,
    dps: 0,
    dmgType: Damage.Kinetic,
    range: 0,
    reloadSeconds: 1,
    splash: 0,
    speed: 0,
    sight: 14,
    radius: 1,
    flags: Flag.Immobile | Flag.Radar,
    footprint: 2,
    power: -25,
    requires: ['refinery'],
    desc: '小地图 + 基础 Intel 产出 + 预知下一个法则。',
    color: 0x5b8fa8,
  }),
  def({
    key: 'turret',
    name: '哨戒炮塔',
    kind: Kind.Building,
    tier: 2,
    tab: 'building',
    costSeu: 900,
    costIntel: 0,
    pop: 0,
    buildSeconds: 14,
    hp: 1000,
    armor: Armor.Structure,
    dps: 62,
    dmgType: Damage.AP,
    range: 7.5,
    reloadSeconds: 1.4,
    splash: 0,
    speed: 0,
    sight: 9,
    radius: 0.9,
    flags: Flag.Immobile | Flag.Projectile,
    footprint: 1,
    power: -20,
    requires: ['factory'],
    desc: '反载具防御。穿甲伤害。',
    color: 0x707a68,
  }),

  // ───────────────── 步兵 ─────────────────
  def({
    key: 'conscript',
    name: '征召新兵',
    kind: Kind.Infantry,
    tier: 1,
    tab: 'infantry',
    costSeu: 150,
    costIntel: 0,
    pop: 1,
    buildSeconds: 6,
    hp: 140,
    armor: Armor.Unarmored,
    dps: 18,
    dmgType: Damage.Kinetic,
    range: 5,
    reloadSeconds: 0.8,
    splash: 0,
    speed: 3.6,
    sight: 7,
    radius: 0.35,
    flags: Flag.None,
    footprint: 0,
    power: 0,
    requires: ['barracks'],
    desc: '造价最低的量产步兵。',
    color: 0xd96a4a,
  }),
  def({
    key: 'engineer',
    name: '工程学徒',
    kind: Kind.Infantry,
    tier: 1,
    tab: 'infantry',
    costSeu: 400,
    costIntel: 0,
    pop: 1,
    buildSeconds: 10,
    hp: 110,
    armor: Armor.Unarmored,
    dps: 0,
    dmgType: Damage.Kinetic,
    range: 1.6,
    reloadSeconds: 1,
    splash: 0,
    speed: 3.8,
    sight: 6,
    radius: 0.35,
    flags: Flag.Builder | Flag.Repairer,
    footprint: 0,
    power: 0,
    requires: ['barracks'],
    desc: '右键受损的友方建筑或载具进行维修（12 HP/s）。无攻击能力。',
    color: 0xe8c07d,
    repairPerSec: 12,
  }),
  def({
    key: 'rifleman',
    name: '前线步兵',
    kind: Kind.Infantry,
    tier: 1,
    tab: 'infantry',
    costSeu: 260,
    costIntel: 0,
    pop: 1,
    buildSeconds: 9,
    hp: 210,
    armor: Armor.Unarmored,
    dps: 27,
    dmgType: Damage.Kinetic,
    range: 5.5,
    reloadSeconds: 1.2,
    splash: 0,
    speed: 3.4,
    sight: 7,
    radius: 0.35,
    flags: Flag.None,
    footprint: 0,
    power: 0,
    requires: ['barracks'],
    desc: '主力步兵。阵列：附近友军越多越耐打。',
    color: 0xc4483a,
  }),
  def({
    key: 'stormtrooper',
    name: '暴风突击兵',
    kind: Kind.Infantry,
    tier: 2,
    tab: 'infantry',
    costSeu: 420,
    costIntel: 0,
    pop: 2,
    buildSeconds: 13,
    hp: 230,
    armor: Armor.Unarmored,
    dps: 34,
    dmgType: Damage.Explosive,
    range: 4,
    reloadSeconds: 1.5,
    splash: 0.9,
    speed: 4.4,
    sight: 7,
    radius: 0.35,
    flags: Flag.Swift,
    footprint: 0,
    power: 0,
    requires: ['barracks', 'refinery'],
    desc: '疾行突击兵。爆破溅射，对建筑与集群有效。',
    color: 0xe07b39,
  }),
  def({
    key: 'guardsman',
    name: '国民警卫军·列兵',
    kind: Kind.Infantry,
    tier: 2,
    tab: 'infantry',
    costSeu: 480,
    costIntel: 20,
    pop: 2,
    buildSeconds: 14,
    hp: 280,
    armor: Armor.Unarmored,
    dps: 42,
    dmgType: Damage.AP,
    range: 6,
    reloadSeconds: 1.6,
    splash: 0,
    speed: 3.2,
    sight: 7.5,
    radius: 0.35,
    flags: Flag.Projectile,
    footprint: 0,
    power: 0,
    requires: ['barracks', 'radar'],
    desc: '反装甲主力。穿甲伤害，克制载具。',
    color: 0x8f3f2f,
  }),

  // ───────────────── 载具 ─────────────────
  def({
    key: 'harvester',
    name: '拾荒车',
    kind: Kind.Harvester,
    tier: 1,
    tab: 'vehicle',
    costSeu: 700,
    costIntel: 0,
    pop: 1,
    buildSeconds: 14,
    hp: 520,
    armor: Armor.Light,
    dps: 0,
    dmgType: Damage.Kinetic,
    range: 0,
    reloadSeconds: 1,
    splash: 0,
    speed: 6.2,
    sight: 6,
    radius: 0.6,
    flags: Flag.CanHarvest,
    footprint: 0,
    power: 0,
    requires: ['refinery'],
    desc: '采集废墟矿点，单趟载量 500 SEU。',
    color: 0xd9a441,
  }),
  def({
    key: 'apc',
    name: '标准运兵车',
    kind: Kind.Vehicle,
    tier: 2,
    tab: 'vehicle',
    costSeu: 600,
    costIntel: 0,
    pop: 2,
    buildSeconds: 15,
    hp: 480,
    armor: Armor.Light,
    dps: 20,
    dmgType: Damage.Kinetic,
    range: 5,
    reloadSeconds: 0.5,
    splash: 0,
    speed: 7.4,
    sight: 8,
    radius: 0.6,
    flags: Flag.Swift | Flag.Crush,
    footprint: 0,
    power: 0,
    requires: ['factory'],
    desc: '高速轻甲载具。可碾压步兵。',
    color: 0xa8763f,
  }),
  def({
    key: 'tank',
    name: '「秩序」主战坦克',
    kind: Kind.Vehicle,
    tier: 3,
    tab: 'vehicle',
    costSeu: 950,
    costIntel: 0,
    pop: 3,
    buildSeconds: 20,
    hp: 820,
    armor: Armor.Heavy,
    dps: 80,
    dmgType: Damage.AP,
    range: 6.5,
    reloadSeconds: 1.8,
    splash: 0,
    speed: 5,
    sight: 8,
    radius: 0.9,
    flags: Flag.Crush | Flag.Projectile,
    footprint: 0,
    power: 0,
    requires: ['factory'],
    desc: '战线脊梁。重甲抗动能，怕穿甲。',
    color: 0x9a4a35,
  }),
  def({
    key: 'artillery',
    name: '罗斯特突击炮',
    kind: Kind.Vehicle,
    tier: 3,
    tab: 'vehicle',
    costSeu: 1300,
    costIntel: 40,
    pop: 4,
    buildSeconds: 26,
    hp: 760,
    armor: Armor.Heavy,
    dps: 115,
    dmgType: Damage.Explosive,
    range: 11,
    reloadSeconds: 3,
    splash: 1.8,
    speed: 4.2,
    sight: 7,
    radius: 0.9,
    flags: Flag.Projectile,
    footprint: 0,
    power: 0,
    requires: ['factory', 'radar'],
    desc: '射程 11 格，超出视野，需友军提供视野。大范围爆破。',
    color: 0x7a3b2a,
  }),
  def({
    key: 'mech',
    name: '重装机甲·铁幕',
    kind: Kind.Vehicle,
    tier: 4,
    tab: 'vehicle',
    costSeu: 2400,
    costIntel: 180,
    pop: 7,
    buildSeconds: 40,
    hp: 2200,
    armor: Armor.Heavy,
    dps: 145,
    dmgType: Damage.AP,
    range: 7,
    reloadSeconds: 2,
    splash: 1.2,
    speed: 3.8,
    sight: 8,
    radius: 1.2,
    flags: Flag.Guard | Flag.Crush | Flag.Projectile,
    footprint: 0,
    power: 0,
    requires: ['factory', 'radar'],
    desc: '传奇重甲。护卫光环：附近友军减伤 20%。攻击溅射。',
    color: 0x6b2f24,
  }),
];

// ─────────────────────────────────────────────────────────────
// 派生查表结构（构造一次，模拟期只读）
// ─────────────────────────────────────────────────────────────

export const DEF_BY_KEY = new Map<string, UnitDef>();
for (const d of DEFS) DEF_BY_KEY.set(d.key, d);

export function defOf(typeId: number): UnitDef {
  return DEFS[typeId - 1]!;
}

export function keyOf(typeId: number): string {
  return DEFS[typeId - 1]!.key;
}

export function idOf(key: string): number {
  const d = DEF_BY_KEY.get(key);
  if (!d) throw new Error(`未知单位 key: ${key}`);
  return d.id;
}

/** 预计算的定点数字段，避免每 tick 重复转换 */
export interface DefFix {
  range: Fix;
  rangeSq: Fix;
  speed: Fix; // 格/tick
  sight: Fix;
  sightSq: Fix;
  radius: Fix;
  splash: Fix;
  splashSq: Fix;
  hitDamage: number;
  reloadTicks: number;
  buildTicks: number;
}

export const DEF_FIX: DefFix[] = DEFS.map((d) => {
  const range = fromFloat(d.range);
  const sight = fromFloat(d.sight);
  const splash = fromFloat(d.splash);
  const reloadTicks = Math.max(1, Math.round(d.reloadSeconds * TICKS_PER_SEC));
  return {
    range,
    rangeSq: mulFix(range, range),
    speed: fromFloat(d.speed / TICKS_PER_SEC),
    sight,
    sightSq: mulFix(sight, sight),
    radius: fromFloat(d.radius),
    splash,
    splashSq: mulFix(splash, splash),
    // 单次伤害 = dps × 装填秒数，保证 DPS 与数值表一致
    hitDamage: Math.max(1, Math.round(d.dps * d.reloadSeconds)),
    reloadTicks,
    buildTicks: Math.max(1, Math.round(d.buildSeconds * TICKS_PER_SEC)),
  };
});

export function fixOf(typeId: number): DefFix {
  return DEF_FIX[typeId - 1]!;
}

/** 本地小工具：避免为了初始化数据表而循环 import fix.ts 的 mul */
function mulFix(a: number, b: number): number {
  const ah = a >> 16;
  const al = a & 0xffff;
  const bh = b >> 16;
  const bl = b & 0xffff;
  return (
    ((Math.imul(ah, bh) << 16) + Math.imul(ah, bl) + Math.imul(al, bh) + ((Math.imul(al, bl) >>> 16) | 0)) | 0
  );
}
